import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import { classNames } from 'primereact/utils'
import { FC, useEffect, useState, useRef } from 'react'
import { Control, Controller, FieldError, FieldErrors, UseFormSetValue } from 'react-hook-form'
import { SelectOption } from '../types/types'
import { Checkbox } from 'primereact/checkbox'
import { CheckboxStyle, DropdownStyle } from '../utils/styling-constants'
import { getCoresPerSlot, Hardware, HardwareLike, normalizeHardware } from '@aero-platform/shared'

type HardwareSettingsSectionProps = {
  sectionKey: string
  sectionIndex: number
  sectionName: string
  control: Control<any>
  isHardwareOverride: boolean
  hardwareOverrides: Hardware
  maxCoresCount: number
  nodes: number
  coresPerNode: number
  setValue: UseFormSetValue<any>
  disabled?: boolean
  mode: string
  formErrors?: FieldErrors
}

const HardwareSettingsSection: FC<HardwareSettingsSectionProps> = ({
  sectionKey,
  sectionIndex,
  control,
  setValue,
  disabled = false,
  formErrors,
  isHardwareOverride: initialIsHardwareOverride,
  hardwareOverrides: initialHardwareOverrides,
  maxCoresCount: initialMaxCoresCount,
  nodes: initialNodes,
  coresPerNode: initialCoresPerNode
}) => {
  const initialHardware = normalizeHardware<HardwareLike>(initialHardwareOverrides || {})
  // Legacy methodologies predate the nodes/coresPerNode metadata and only carry
  // hardwareOverrides.slots/coresPerSlot. Fall back to the normalized legacy hardware so these
  // refs don't start at 1 node / 0 cores (which makes editing Nodes write a zero total core count).
  const refNodes = useRef<number>(initialNodes || Number(initialHardware.slots) || 1)
  const refCoresPerNode = useRef<number>(
    initialCoresPerNode || Number(initialHardware.coresPerSlot) || 0
  )
  const fieldPrefix = `selectedSystems.${sectionIndex}`

  const [coreTypes, setCoreTypes] = useState<SelectOption[]>([
    { value: 'hematite', label: 'Hematite' },
    { value: 'natrolite', label: 'Natrolite' },
    { value: 'raspite', label: 'Raspite' },
    { value: 'iolite-1-16core', label: 'Iolite-1 16Core' }
  ])
  const [isHardwareOverride, setIsHardwareOverride] = useState<boolean>(
    initialIsHardwareOverride || false
  )
  const [actualCores, setActualCores] = useState<number>(
    (refNodes.current || 1) * (refCoresPerNode.current || 0)
  )

  useEffect(() => {
    // Mirror the legacy fallback above and keep the refs in sync when props change, so switching
    // to a previously saved methodology restores its true nodes/cores instead of 1 node / 0 cores.
    const normalized = normalizeHardware<HardwareLike>(initialHardwareOverrides || {})
    const nextNodes = initialNodes || Number(normalized.slots) || 1
    const nextCoresPerNode = initialCoresPerNode || Number(normalized.coresPerSlot) || 0
    refNodes.current = nextNodes
    refCoresPerNode.current = nextCoresPerNode
    setIsHardwareOverride(initialIsHardwareOverride || false)
    setActualCores(nextNodes * nextCoresPerNode)
    setValue(`${fieldPrefix}.hardwareOverrides.coresPerSlot`, nextNodes * nextCoresPerNode)
    setValue(`${fieldPrefix}.hardwareOverrides.coresPerNode`, nextCoresPerNode)
    setValue(`${fieldPrefix}.hardwareOverrides.nodes`, nextNodes)
    setValue(`${fieldPrefix}.hardwareOverrides.slots`, 0)
  }, [initialIsHardwareOverride, initialHardwareOverrides, initialCoresPerNode, initialNodes])

  const updateCores = (nodes: number, coreType: string) => {
    refCoresPerNode.current = getCoresPerSlot(nodes, coreType)
    setActualCores(nodes * refCoresPerNode.current)
    setValue(`${fieldPrefix}.hardwareOverrides.coresPerSlot`, nodes * refCoresPerNode.current)
    setValue(`${fieldPrefix}.coresPerNode`, refCoresPerNode.current)
    setValue(`${fieldPrefix}.nodes`, nodes)
  }

  const getFormErrorMessage = name => {
    if (!formErrors) {
      return <small className="error">&nbsp;</small>
    }
    const parts = name.split('.')
    let errors: FieldError | FieldErrors | null = formErrors
    for (let i = 0; i < parts.length; i++) {
      if (errors[parts[i]]) {
        errors = errors[parts[i]]
      } else {
        errors = null
        break
      }
    }
    return errors ? (
      <small className="error">{errors.message as string}</small>
    ) : (
      <small className="error">&nbsp;</small>
    )
  }

  return (
    <div>
      <div className="field-box col-span-1 mt-4 border-t-2 pt-4">
        <div className="ml-4 flex-grow flex items-center">
          <Checkbox
            inputId={`${sectionKey}-hardware-override-checkbox`}
            name={`${sectionKey}-hardware-override-checkbox`}
            pt={CheckboxStyle}
            checked={isHardwareOverride}
            disabled={disabled}
            onChange={e => {
              setIsHardwareOverride(e.checked)
              setValue(`${fieldPrefix}.isHardwareOverride`, e.checked)
            }}
          />
          <label
            htmlFor={`${sectionKey}-hardware-override-checkbox`}
            className="ml-2 font-normal cursor-pointer">
            {`Override Default Hardware Settings`}
          </label>
        </div>
      </div>
      <div className="w-full grid grid-cols-12 gap-x-2 gap-y-4 mt-8 pb-10">
        <div className="field-box col-span-2">
          <Controller
            name={`${fieldPrefix}.hardwareOverrides.coreType`}
            control={control}
            render={({ field, fieldState }) => (
              <>
                <label htmlFor={`${sectionKey}-coreType`} className="mb-2">
                  {`Core Type`}
                </label>
                <Dropdown
                  id={`${sectionKey}-coreType`}
                  value={field.value}
                  options={coreTypes}
                  disabled={disabled || !isHardwareOverride}
                  filter
                  resetFilterOnHide={false}
                  emptyFilterMessage="No matching Core Type found"
                  pt={DropdownStyle}
                  onChange={e => {
                    field.onChange(e.value)
                    if (e.value) {
                      updateCores(refNodes.current || 1, e.value)
                    }
                  }}
                  className={classNames({ error: fieldState.error })}
                  placeholder="Select Core Type"
                  optionLabel="label"
                  optionValue="value"
                />
                {getFormErrorMessage(`${fieldPrefix}.hardwareOverrides.coreType`)}
              </>
            )}
          />
        </div>
        <div className="field-box col-span-2">
          <Controller
            name={`${fieldPrefix}.nodes`}
            control={control}
            render={({ field, fieldState }) => (
              <>
                <label htmlFor={`${sectionKey}-nodes`} className="mb-2">
                  {`Nodes`}
                </label>
                <InputNumber
                  id={`${sectionKey}-nodes`}
                  useGrouping={false}
                  min={0}
                  disabled={disabled || !isHardwareOverride}
                  value={field.value}
                  onChange={e => {
                    field.onChange(e.value)
                    refNodes.current = e.value
                    if (e.value) {
                      setValue(
                        `${fieldPrefix}.hardwareOverrides.coresPerSlot`,
                        e.value * refCoresPerNode.current
                      )
                      setActualCores(e.value * refCoresPerNode.current)
                    }
                  }}
                  className={classNames({ error: fieldState.error })}
                  placeholder="Enter number of nodes"
                />
                {getFormErrorMessage(`${fieldPrefix}.nodes`)}
              </>
            )}
          />
        </div>
        <div className="field-box col-span-1">
          <label htmlFor={`${sectionKey}-cores`} className="mb-2">
            {`Cores Per Node`}
          </label>
          <p className={`p-1 text-lg ${!isHardwareOverride ? 'text-gray-400' : ''}`}>
            {refCoresPerNode.current || 0}
          </p>
        </div>
        <div className="field-box col-span-1">
          <label htmlFor={`${sectionKey}-totalCores`} className="mb-2">
            {`Total Cores`}
          </label>
          <p className={`p-1 text-lg ${!isHardwareOverride ? 'text-gray-400' : ''}`}>
            {actualCores || 0}
          </p>
        </div>
        <div className="field-box col-span-2">
          <Controller
            name={`${fieldPrefix}.maxCoresCount`}
            control={control}
            render={({ field, fieldState }) => (
              <>
                <label htmlFor={`${sectionKey}-maxCoresCount`} className="mb-2">
                  {`Max Cores Count`}
                </label>
                <InputNumber
                  id={`${sectionKey}-maxCoresCount`}
                  useGrouping={false}
                  min={0}
                  disabled={disabled || !isHardwareOverride}
                  value={field.value}
                  onChange={e => {
                    field.onChange(e.value)
                  }}
                  className={classNames({ error: fieldState.error })}
                  placeholder="Enter max cores count"
                />
                {getFormErrorMessage(`${fieldPrefix}.maxCoresCount`)}
              </>
            )}
          />
        </div>
        <div className="field-box col-span-2">
          <Controller
            name={`${fieldPrefix}.hardwareOverrides.walltime`}
            control={control}
            render={({ field, fieldState }) => (
              <>
                <label htmlFor={`${sectionKey}-walltime`} className="mb-2">
                  {`Walltime (Hrs)`}
                </label>
                <InputNumber
                  id={`${sectionKey}-walltime`}
                  useGrouping={false}
                  min={0}
                  disabled={disabled || !isHardwareOverride}
                  value={field.value}
                  onChange={e => {
                    field.onChange(e.value)
                  }}
                  className={classNames({ error: fieldState.error })}
                  placeholder="Enter walltime"
                />
                {getFormErrorMessage(`${fieldPrefix}.hardwareOverrides.walltime`)}
              </>
            )}
          />
        </div>
      </div>
    </div>
  )
}

export default HardwareSettingsSection
